import api, { queryClient } from "@app/config/api/api";
import type { operations } from "@generated/openapi";
import type { ExperimentId } from "@globals";
import { notifications } from "@lib/notification/notification";

export const userDetailsOptions = api.queryOptions("get", "/iam/user/", {
  queryKey: ["iam", "user"]
});
export const recentsOptions = api.queryOptions("get", "/iam/user/recents");
export const favoritesOptions = api.queryOptions("get", "/iam/user/favorites");

export const useCreateTeam = () =>
  api.useMutation("post", "/iam/teams/", {
    onSuccess: async team => {
      await queryClient.invalidateQueries({
        queryKey: api.queryOptions("get", "/iam/organizations/{organization_id}/teams/", {
          params: { path: { organization_id: team.organization.id } }
        }).queryKey
      });
      notifications.success("Team created successfully");
    }
  });

export const useUpdateTeam = () =>
  api.useMutation("patch", "/iam/teams/{team_id}", {
    onSuccess: async team => {
      await queryClient.invalidateQueries({
        queryKey: api.queryOptions("get", "/iam/organizations/{organization_id}/teams/", {
          params: { path: { organization_id: team.organization.id } }
        }).queryKey
      });
      notifications.success("Team updated successfully");
    }
  });

export const useDeleteTeam = (organizationId: string) =>
  api.useMutation("delete", "/iam/teams/{team_id}", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: api.queryOptions("get", "/iam/organizations/{organization_id}/teams/", {
          params: { path: { organization_id: organizationId } }
        }).queryKey
      });
      notifications.success("Team deleted successfully");
    }
  });

export const useOrganizationUsers = (
  organizationId: string,
  query?: operations["list_organization_users"]["parameters"]["query"]
) =>
  api.useQuery(
    "get",
    "/iam/organizations/{organization_id}/users/",
    {
      params: { path: { organization_id: organizationId }, query },
      queryKey: ["iam", "organizations"]
    },
    {
      enabled: Boolean(organizationId)
    }
  );

export const useOrganizationTeams = (
  organizationId: string,
  query?: operations["list_organization_teams"]["parameters"]["query"]
) =>
  api.useQuery(
    "get",
    "/iam/organizations/{organization_id}/teams/",
    {
      params: { path: { organization_id: organizationId }, query },
      queryKey: ["iam", "organizations"]
    },
    {
      enabled: Boolean(organizationId)
    }
  );

export const useAddTeamRole = (organization_id: string) =>
  api.useMutation("post", "/iam/teams/{team_id}:add_role", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: api.queryOptions("get", "/iam/organizations/{organization_id}/users/", {
          params: { path: { organization_id: organization_id } }
        }).queryKey
      });
      notifications.success("User added to team successfully");
    }
  });

export const useUpdateTeamRole = (organization_id: string) =>
  api.useMutation("put", "/iam/teams/{team_id}/roles/{mapping_id}", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: api.queryOptions("get", "/iam/organizations/{organization_id}/users/", {
          params: { path: { organization_id: organization_id } }
        }).queryKey
      });
      notifications.success("Team role updated successfully");
    }
  });

export const useRemoveTeamRole = (organization_id: string) =>
  api.useMutation("delete", "/iam/teams/{team_id}/roles/{mapping_id}", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: api.queryOptions("get", "/iam/organizations/{organization_id}/users/", {
          params: { path: { organization_id: organization_id } }
        }).queryKey
      });
      notifications.success("User removed from team successfully");
    }
  });

export const useMoveExperimentToTeam = () => api.useMutation("put", "/iam/transfer_experiment");

export const useFavorites = () => api.useQuery("get", "/iam/user/favorites");
export const invalidateFavoriteExperiments = () => queryClient.invalidateQueries(favoritesOptions);

export const useRecents = () => api.useQuery("get", "/iam/user/recents");

export const useActivity = (query?: operations["list_activity"]["parameters"]["query"]) =>
  api.useQuery("get", "/iam/activity/", { params: { query } });

export const useIsFavoriteExperiment = (
  experiment_id: ExperimentId,
  { enabled }: { enabled?: boolean } = { enabled: true }
) => api.useQuery("get", "/experiments/{experiment_id}:favorite", { params: { path: { experiment_id } } }, { enabled });

export const useToggleFavoriteExperiment = () =>
  api.useMutation("post", "/experiments/{experiment_id}:favorite", {
    onSuccess: async () => {
      await invalidateFavoriteExperiments();
    }
  });
